import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import CockpitNavigation from './CockpitNavigation';
import { chargerCockpitOperationnel } from './cockpitOperationnelApi';

const useStyles = makeStyles((theme) => ({
  page: { paddingTop: theme.spacing(3), paddingBottom: theme.spacing(4), textAlign: 'left' },
  paper: { padding: theme.spacing(2), marginBottom: theme.spacing(2), boxShadow: 'none', border: '1px solid #e5e7eb' },
  header: { display: 'flex', justifyContent: 'space-between', gap: theme.spacing(2), flexWrap: 'wrap', alignItems: 'center' },
  muted: { color: '#6b7280', fontSize: 12 },
  grilleScore: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: theme.spacing(1), marginTop: theme.spacing(1.5) },
  blocScore: { border: '1px solid #e5e7eb', borderRadius: 6, padding: theme.spacing(0.75), backgroundColor: '#fff' },
  blocScoreCliquable: { cursor: 'pointer', '&:hover': { borderColor: '#93c5fd', backgroundColor: '#eff6ff' } },
  valeur: { fontWeight: 700, fontSize: 18, lineHeight: 1.18, wordBreak: 'break-word' },
  label: { color: '#6b7280', fontSize: 11, textTransform: 'uppercase' },
  tableScroll: { overflowX: 'auto' },
  rowClickable: { cursor: 'pointer', '&:hover': { backgroundColor: '#f9fafb' } },
  rowSelectionnee: { backgroundColor: '#eef5ff' },
  panneauDetail: { minHeight: 420, overflow: 'hidden' },
  resumeIncident: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: theme.spacing(1), marginTop: theme.spacing(1.25) },
  timeline: { backgroundColor: '#111827', color: '#f9fafb', padding: theme.spacing(1), borderRadius: 4, fontFamily: 'monospace', fontSize: 11, lineHeight: 1.35, marginTop: theme.spacing(1), overflowX: 'hidden', maxHeight: 360, overflowY: 'auto' },
  ligneTimeline: {
    display: 'grid',
    gridTemplateColumns: '142px 56px minmax(120px, 0.8fr) minmax(140px, 1fr) minmax(0, 1.6fr) 72px',
    gap: theme.spacing(0.75),
    padding: '6px 4px',
    alignItems: 'start',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(0.25),
    },
  },
  ligneTimelineClickable: { cursor: 'pointer', borderTop: '1px solid rgba(255,255,255,0.06)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' } },
  timelineCell: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', wordBreak: 'break-word' },
  timelineCellWrap: { minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' },
  ligneBruteComplete: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', color: '#e5e7eb', backgroundColor: '#030712', border: '1px solid #374151', borderRadius: 4, padding: theme.spacing(1), marginTop: theme.spacing(0.5), marginBottom: theme.spacing(1), maxWidth: '100%', overflowX: 'hidden' },
  preuvePrincipale: { border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', borderRadius: 6, padding: theme.spacing(1.5), marginTop: theme.spacing(1.5) },
  valeurPreuve: { fontFamily: 'monospace', fontWeight: 700, fontSize: 16, lineHeight: 1.3, wordBreak: 'break-word' },
  actionsPreuve: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1), alignItems: 'center' },
  actionsCompactes: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.75), alignItems: 'center' },
  listeSimple: { marginTop: theme.spacing(1), paddingLeft: theme.spacing(2.5) },
  chipVert: { backgroundColor: '#ecfdf5', color: '#065f46', fontSize: 11 },
  chipOrange: { backgroundColor: '#fff7ed', color: '#9a3412', fontSize: 11 },
  chipRouge: { backgroundColor: '#fef2f2', color: '#991b1b', fontSize: 11 },
  chipGris: { backgroundColor: '#f3f4f6', color: '#374151', fontSize: 11 },
  erreur: { borderLeft: '4px solid #f59e0b', padding: theme.spacing(1.5), backgroundColor: '#fff7ed', marginTop: theme.spacing(1.5) },
}));

const CockpitSanteRecente = () => {
  const classes = useStyles();
  const history = useHistory();
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [episodeSelectionneId, setEpisodeSelectionneId] = useState('');
  const [journeeSelectionnee, setJourneeSelectionnee] = useState(jourIsoRelative(-1));
  const [afficherSeulementSuspects, setAfficherSeulementSuspects] = useState(false);

  const charger = (journee = journeeSelectionnee) => {
    // DEBUG navigation
    console.log(`[CockpitSanteRecente] charger() — journee demandée: ${journee}`);
    setChargement(true);
    setDonnees(null);
    chargerCockpitOperationnel({ journee })
      .then((payload) => {
        // DEBUG navigation
        const incidents = payload.incidentsCritiques || payload.incidents || [];
        const journeesCamera = payload.journeesCamera || [];
        const jours = Array.from(new Set(journeesCamera.map((j) => j.journee_captation).filter(Boolean)));
        console.log(`[CockpitSanteRecente] données reçues — ${incidents.length} incidents, journeesCamera: [${jours.join(', ')}]`);
        setDonnees(payload);
        setErreur('');
        setChargement(false);
      })
      .catch((err) => {
        setErreur(err.message);
        setChargement(false);
      });
  };

  useEffect(() => {
    // DEBUG navigation
    console.log(`[CockpitSanteRecente] useEffect journeeSelectionnee → ${journeeSelectionnee}`);
    charger(journeeSelectionnee);
  }, [journeeSelectionnee]);

  const sante = useMemo(() => calculerSanteRecente(donnees, journeeSelectionnee), [donnees, journeeSelectionnee]);
  const incidentsAffiches = sante.incidentsGroupes || sante.incidentsRecents;
  const episodeSelectionne = useMemo(() => (
    (incidentsAffiches || []).find((incident) => idEpisodeIncident(incident) === episodeSelectionneId)
    || incidentsAffiches[0]
    || null
  ), [incidentsAffiches, episodeSelectionneId]);

  useEffect(() => {
    if (!episodeSelectionneId && incidentsAffiches.length > 0) {
      setEpisodeSelectionneId(idEpisodeIncident(incidentsAffiches[0]));
    }
    if (episodeSelectionneId && !incidentsAffiches.some((incident) => idEpisodeIncident(incident) === episodeSelectionneId)) {
      setEpisodeSelectionneId(incidentsAffiches[0] ? idEpisodeIncident(incidentsAffiches[0]) : '');
    }
  }, [incidentsAffiches, episodeSelectionneId]);

  return (
    <Container maxWidth={false} className={classes.page}>
      <CockpitNavigation vueActive="sante" />
      <Paper className={classes.paper}>
        <div className={classes.header}>
          <div>
            <Typography variant="h4">Sante recente systeme</Typography>
            <Typography variant="body2" className={classes.muted}>
              Recul court terme: incidents, faux succes, dette active et stabilite observee.
            </Typography>
          </div>
          <Button variant="outlined" size="small" onClick={() => charger(journeeSelectionnee)} disabled={chargement}>
            {chargement ? 'Chargement...' : 'Actualiser'}
          </Button>
        </div>
        <NavigationJournee
          classes={classes}
          journee={journeeSelectionnee}
          chargement={chargement}
          changerJournee={(j) => {
            // DEBUG navigation
            console.log(`[CockpitSanteRecente] changerJournee → ${j} (était: ${journeeSelectionnee})`);
            setJourneeSelectionnee(j);
          }}
        />
        {erreur && (
          <div className={classes.erreur}>
            <Typography variant="subtitle2">Erreur backend</Typography>
            <Typography variant="body2">{erreur}</Typography>
          </div>
        )}
        {donnees && (
          <ScoreSante
            sante={sante}
            classes={classes}
            onMatchsSuspectsClick={() => setAfficherSeulementSuspects(true)}
          />
        )}
      </Paper>

      {donnees && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <SectionIncidentsRecents
              items={sante.incidentsRecents}
              itemsGroupes={incidentsAffiches}
              classes={classes}
              incidentSelectionne={episodeSelectionne}
              onSelectionner={(incident) => setEpisodeSelectionneId(idEpisodeIncident(incident))}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <PanneauDetailIncident
              incident={episodeSelectionne}
              incidents={sante.incidentsRecents}
              classes={classes}
              ouvrirInvestigation={(incident) => history.push(urlInvestigationCamera(incident))}
            />
          </Grid>
          <Grid item xs={12}>
            <SectionMatchsRecents
              matchs={sante.matchsRecents}
              classes={classes}
              seulementSuspects={afficherSeulementSuspects}
              changerSeulementSuspects={setAfficherSeulementSuspects}
            />
          </Grid>
          <Grid item xs={12}>
            <SectionCamerasPeriode
              cameras={sante.camerasPeriode}
              classes={classes}
              ouvrirInvestigation={(camera) => history.push(urlInvestigationCameraDepuisCamera(camera, journeeSelectionnee))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionSignauxRecents sante={sante} classes={classes} />
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionVersions versions={sante.versionsFragiles} classes={classes} />
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

const ScoreSante = ({ sante, classes, onMatchsSuspectsClick }) => (
  <div className={classes.grilleScore}>
    <Kpi classes={classes} label="Score couverture match" valeur={`${sante.score}/100`} />
    <Kpi
      classes={classes}
      label="Matchs suspects"
      valeur={sante.nbMatchsSuspects}
      alerte={sante.nbMatchsSuspects > 0}
      onClick={onMatchsSuspectsClick}
    />
    <Kpi classes={classes} label="Anomalies couverture" valeur={sante.nbAnomaliesCouverture} alerte={sante.nbAnomaliesCouverture > 0} />
    <Kpi classes={classes} label="Incidents critiques" valeur={sante.nbIncidentsCritiques} alerte={sante.nbIncidentsCritiques > 0} />
  </div>
);

const NavigationJournee = ({ classes, journee, chargement, changerJournee }) => (
  <div className={classes.header} style={{ marginTop: 16 }}>
    <div>
      <Typography variant="subtitle2">Journee inspectee</Typography>
      <Typography variant="body2" className={classes.muted}>
        {chargement
          ? 'Chargement en cours...'
          : 'La vue demarre sur hier pour analyser la derniere soiree complete.'}
      </Typography>
    </div>
    <div className={classes.actionsPreuve}>
      <Button variant="outlined" size="small" disabled={chargement} onClick={() => changerJournee(decalerJourIso(journee, -1))}>
        {chargement ? '...' : 'Avant'}
      </Button>
      <Button variant="outlined" size="small" disabled={chargement} onClick={() => changerJournee(jourIsoRelative(-1))}>
        {chargement ? '...' : 'Hier'}
      </Button>
      <Button variant="outlined" size="small" disabled={chargement} onClick={() => changerJournee(jourIsoRelative(0))}>
        {chargement ? '...' : "Aujourd'hui"}
      </Button>
      <Button
        variant="outlined"
        size="small"
        disabled={chargement || journee >= jourIsoRelative(0)}
        onClick={() => changerJournee(decalerJourIso(journee, 1))}
      >
        {chargement ? '...' : 'Apres'}
      </Button>
      <TextField
        type="date"
        variant="outlined"
        size="small"
        value={journee}
        disabled={chargement}
        onChange={(event) => changerJournee(event.target.value)}
        inputProps={{ 'aria-label': 'Journee inspectee', max: jourIsoRelative(0) }}
      />
    </div>
  </div>
);

const SectionIncidentsRecents = ({ items, itemsGroupes, classes, incidentSelectionne, onSelectionner }) => (
  <Paper className={classes.paper}>
    <Typography variant="h6">Incidents de la periode ({(itemsGroupes || []).length} episodes)</Typography>
    <Typography variant="body2" className={classes.muted}>
      Regroupement leger par camera et proximite temporelle. {items.length} signaux bruts dans la periode.
    </Typography>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Signal</TableCell>
          <TableCell>Camera</TableCell>
          <TableCell>Age</TableCell>
          <TableCell>Severite</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(itemsGroupes || []).map((incident, index) => {
          const representant = incident.representant || incident;
          return (
          <TableRow
            key={idEpisodeIncident(incident, index)}
            className={`${classes.rowClickable} ${incidentSelectionne && idEpisodeIncident(incidentSelectionne) === idEpisodeIncident(incident) ? classes.rowSelectionnee : ''}`}
            onClick={() => onSelectionner(incident)}
          >
            <TableCell>
              <strong>{incident.libelleScenario || extraireEvenementPrincipal(representant, [representant])}</strong>
              {incident.nbSignaux > 1 && (
                <Typography variant="body2" className={classes.muted}>
                  {incident.nbSignaux} signaux regroupes
                </Typography>
              )}
            </TableCell>
            <TableCell>{representant.cam_id || representant.tel_id || '-'}</TableCell>
            <TableCell>{ageDepuis(representant.ts_event || representant.recu_at)}</TableCell>
            <TableCell>
              <Chip size="small" label={representant.severite_operationnelle || representant.niveau || 'a surveiller'} className={classeSeverite(representant, classes)} />
            </TableCell>
          </TableRow>
          );
        })}
        {(itemsGroupes || []).length === 0 && (
          <TableRow><TableCell colSpan={4}>Aucun incident recent expose.</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  </Paper>
);

const PanneauDetailIncident = ({ incident, incidents, classes, ouvrirInvestigation }) => {
  const [lignesOuvertes, setLignesOuvertes] = useState({});
  const [copieEtat, setCopieEtat] = useState('');
  const representant = representantIncident(incident);

  useEffect(() => {
    setLignesOuvertes({});
    setCopieEtat('');
  }, [incident && idEpisodeIncident(incident)]);

  if (!representant) {
    return (
      <Paper className={`${classes.paper} ${classes.panneauDetail}`}>
        <Typography variant="h6">Detail operationnel</Typography>
        <Typography variant="body2" className={classes.muted}>
          Aucun incident recent a inspecter.
        </Typography>
      </Paper>
    );
  }

  const timeline = construireTimelineIncident(incident, incidents);
  const interpretation = interpreterIncident(representant, timeline);
  const recommandations = recommanderPistes(representant, interpretation);
  const evenementPrincipal = extraireEvenementPrincipal(representant, timeline);
  const classeIncident = classeTechniqueIncident(representant, timeline);
  const severite = severiteLisible(representant);
  const metas = metaIncident(representant);
  const ligneBrutePrincipale = ligneBruteIncident(representant);
  const copier = (libelle, texte) => {
    copierTexte(texte).then(() => {
      setCopieEtat(`${libelle} copie`);
      setTimeout(() => setCopieEtat(''), 1800);
    });
  };
  const basculerLigne = (cle) => setLignesOuvertes((etat) => ({ ...etat, [cle]: !etat[cle] }));

  return (
    <Paper className={`${classes.paper} ${classes.panneauDetail}`}>
      <div className={classes.header}>
        <div>
          <Typography variant="h6">Detail operationnel</Typography>
          <Typography variant="body2" className={classes.muted}>
            Preuve brute visible immediatement, avec pistes simples.
          </Typography>
        </div>
        <div className={classes.actionsPreuve}>
          {copieEtat && <Typography variant="body2" className={classes.muted}>{copieEtat}</Typography>}
          <Button variant="outlined" size="small" onClick={() => copier('Incident complet', construireIncidentCopiable(representant, timeline))}>
            Copier incident complet
          </Button>
          <Button variant="outlined" size="small" onClick={() => ouvrirInvestigation(incident)}>
            Investigation
          </Button>
        </div>
      </div>

      <div className={classes.preuvePrincipale}>
        <Typography variant="body2" className={classes.muted}>Evenement principal detecte</Typography>
        <div className={classes.valeurPreuve}>{evenementPrincipal}</div>
        <div className={classes.actionsPreuve} style={{ marginTop: 8 }}>
          <Chip size="small" label={`severite: ${severite}`} className={classeSeverite(representant, classes)} />
          <Chip size="small" label={`classe: ${classeIncident}`} className={classes.chipGris} />
          <Chip size="small" label={`level: ${metas.level}`} className={classes.chipGris} />
          <Chip size="small" label={`source: ${metas.source}`} className={classes.chipGris} />
          <Chip size="small" label={`domaine: ${metas.domaine}`} className={classes.chipGris} />
          <Chip size="small" label={`cameraId: ${metas.cameraId}`} className={classes.chipGris} />
        </div>
        <Typography variant="body2" style={{ marginTop: 10 }}>
          <strong>Timestamp complet:</strong> {formaterTimestampComplet(representant.ts_event || representant.recu_at)}
        </Typography>
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Ligne brute</summary>
          <pre className={classes.ligneBruteComplete}>{ligneBrutePrincipale}</pre>
          <Button size="small" variant="outlined" onClick={() => copier('Ligne brute', ligneBrutePrincipale)}>
            Copier ligne brute
          </Button>
        </details>
      </div>

      <div className={classes.resumeIncident}>
        <Kpi classes={classes} label="Camera" valeur={representant.cam_id || representant.tel_id || 'n/d'} />
        <Kpi classes={classes} label="Periode" valeur={periodeIncident(timeline)} />
        <Kpi classes={classes} label="Evenement principal" valeur={evenementPrincipal} />
        <Kpi classes={classes} label="Classe" valeur={classeIncident} />
        <Kpi classes={classes} label="Severite" valeur={severite} alerte={severite === 'critique'} />
        <Kpi classes={classes} label="Signaux episode" valeur={timeline.length} />
        <Kpi classes={classes} label="Duree observee" valeur={dureeTimeline(timeline)} />
        <Kpi classes={classes} label="Convergence" valeur={interpretation.convergence} alerte={interpretation.convergence !== 'probable'} />
        <Kpi classes={classes} label="Recovery" valeur={interpretation.recovery} alerte={interpretation.recovery === 'incomplet'} />
      </div>

      <div className={classes.header} style={{ marginTop: 16 }}>
        <Typography variant="subtitle2">Timeline brute immediate</Typography>
        <Button size="small" variant="outlined" onClick={() => copier('Timeline brute', construireTimelineCopiable(timeline))}>
          Copier timeline
        </Button>
      </div>
      <div className={classes.timeline}>
        {timeline.map((item, index) => (
          <React.Fragment key={idIncident(item, index)}>
            {(() => {
              const metasLigne = metaIncident(item);
              const ligneOuverte = lignesOuvertes[idIncident(item, index)];
              return (
                <React.Fragment>
            <div
              className={`${classes.ligneTimeline} ${classes.ligneTimelineClickable}`}
              onClick={() => basculerLigne(idIncident(item, index))}
            >
              <span className={classes.timelineCell} style={{ color: '#9ca3af' }}>{formaterTimestampComplet(item.ts_event || item.recu_at)}</span>
              <span className={classes.timelineCell}>{metasLigne.level}</span>
              <span className={classes.timelineCell} style={{ color: '#f9fafb', fontWeight: 700 }}>{extraireEvenementPrincipal(item, [item])}</span>
              <span className={classes.timelineCellWrap} style={{ color: '#cbd5e1' }}>
                {metasLigne.source}
                {metasLigne.domaine !== 'n/d' ? ` / ${metasLigne.domaine}` : ''}
                {metasLigne.cameraId !== 'n/d' ? ` / cam ${metasLigne.cameraId}` : ''}
              </span>
              <span className={ligneOuverte ? classes.timelineCellWrap : classes.timelineCell} style={{ color: '#d1d5db' }}>
                {ligneBruteIncident(item)}
              </span>
              <Button
                size="small"
                variant="outlined"
                onClick={(event) => {
                  event.stopPropagation();
                  copier('Ligne', ligneBruteIncident(item));
                }}
                style={{ color: '#f9fafb', borderColor: '#4b5563' }}
              >
                Copier
              </Button>
            </div>
            {ligneOuverte && (
              <pre className={classes.ligneBruteComplete}>{ligneBruteIncident(item)}</pre>
            )}
                </React.Fragment>
              );
            })()}
          </React.Fragment>
        ))}
      </div>

      <Grid container spacing={2} style={{ marginTop: 8 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2">Pistes utiles</Typography>
          <ul className={classes.listeSimple}>
            {interpretation.pistes.map((piste) => <li key={piste}>{piste}</li>)}
          </ul>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2">Pistes recommandees</Typography>
          <ul className={classes.listeSimple}>
            {recommandations.map((piste) => <li key={piste}>{piste}</li>)}
          </ul>
        </Grid>
      </Grid>
    </Paper>
  );
};

const SectionMatchsRecents = ({ matchs, classes, seulementSuspects, changerSeulementSuspects }) => {
  const matchsAffiches = seulementSuspects ? matchs.filter((match) => match.anomalies.length > 0) : matchs;
  return (
  <Paper className={classes.paper}>
    <div className={classes.header}>
      <div>
        <Typography variant="h6">
          {seulementSuspects ? `Matchs suspects (${matchsAffiches.length})` : 'Matchs recents enregistres'}
        </Typography>
        <Typography variant="body2" className={classes.muted}>
          Projection prudente de couverture: cameras observees, angles video, clips et buts sans preuve video.
        </Typography>
      </div>
      <div className={classes.actionsCompactes}>
        <Button
          size="small"
          variant={seulementSuspects ? 'contained' : 'outlined'}
          color={seulementSuspects ? 'primary' : 'default'}
          onClick={() => changerSeulementSuspects(true)}
        >
          Suspects
        </Button>
        {seulementSuspects && (
          <Button size="small" variant="outlined" onClick={() => changerSeulementSuspects(false)}>
            Tous
          </Button>
        )}
      </div>
    </div>
    <div className={classes.tableScroll}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Match</TableCell>
            <TableCell>Cameras observees</TableCell>
            <TableCell>Evenements</TableCell>
            <TableCell>Videos</TableCell>
            <TableCell>Etat general</TableCell>
            <TableCell>Anomalies</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {matchsAffiches.slice(0, 50).map((match) => (
            <TableRow key={match.id}>
              <TableCell>
                <strong>{match.libelle}</strong>
                {match.sourceFallback && (
                  <Typography variant="body2" className={classes.muted}>projection journee camera</Typography>
                )}
              </TableCell>
              <TableCell>{match.nbCameras}</TableCell>
              <TableCell>{match.nbEvenements}</TableCell>
              <TableCell>{match.nbVideos}</TableCell>
              <TableCell>
                <Chip size="small" label={match.etatGeneral} className={classeStabilite(match.etatGeneral, classes)} />
              </TableCell>
              <TableCell>
                {match.anomalies.length > 0
                  ? (
                    <ul className={classes.listeSimple} style={{ margin: 0 }}>
                      {match.anomalies.map((anomalie) => (
                        <li key={anomalie.cle}>
                          <Chip
                            size="small"
                            label={anomalie.libelle}
                            className={anomalie.niveau === 'critique' ? classes.chipRouge : classes.chipOrange}
                            style={{ marginRight: 4, marginBottom: 4 }}
                          />
                          {anomalie.description && <span>{anomalie.description}</span>}
                        </li>
                      ))}
                    </ul>
                  )
                  : <span className={classes.muted}>Aucune anomalie metier exposee</span>}
              </TableCell>
            </TableRow>
          ))}
          {matchsAffiches.length > 50 && (
            <TableRow><TableCell colSpan={6}>+{matchsAffiches.length - 50} matchs supplementaires dans la periode.</TableCell></TableRow>
          )}
          {matchsAffiches.length === 0 && (
            <TableRow><TableCell colSpan={6}>Aucun match ou journee camera recente exposee.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  </Paper>
  );
};

const SectionCamerasPeriode = ({ cameras, classes, ouvrirInvestigation }) => (
  <Paper className={classes.paper}>
    <Typography variant="h6">Cameras pertinentes de la periode ({cameras.length})</Typography>
    <Typography variant="body2" className={classes.muted}>
      Cameras avec activite, incident, match, dette ou journee camera dans la periode selectionnee.
    </Typography>
    <div className={classes.tableScroll}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Camera</TableCell>
            <TableCell>Dernier signal</TableCell>
            <TableCell>Activite</TableCell>
            <TableCell>Incidents</TableCell>
            <TableCell>Matchs</TableCell>
            <TableCell>Investigation</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cameras.map((camera) => (
            <TableRow key={camera.id}>
              <TableCell><strong>{camera.id}</strong></TableCell>
              <TableCell>{formaterTimestampComplet(camera.dernierSignalAt)}</TableCell>
              <TableCell>{camera.sources.join(', ') || 'activite'}</TableCell>
              <TableCell>{camera.nbIncidents}</TableCell>
              <TableCell>{camera.nbMatchs}</TableCell>
              <TableCell>
                <Button size="small" variant="outlined" onClick={() => ouvrirInvestigation(camera)}>
                  Ouvrir
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {cameras.length === 0 && (
            <TableRow><TableCell colSpan={6}>Aucune camera pertinente exposee pour cette journee.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  </Paper>
);

const SectionSignauxRecents = ({ sante, classes }) => (
  <Paper className={classes.paper}>
    <Typography variant="h6">Signaux techniques secondaires</Typography>
    <Typography variant="body2" className={classes.muted}>
      Signaux utiles pour enquete, mais secondaires par rapport a la couverture match.
    </Typography>
    <div className={classes.grilleScore}>
      <Kpi classes={classes} label="Uploads bloques" valeur={sante.uploadsBloques} alerte={sante.uploadsBloques > 0} />
      <Kpi classes={classes} label="Etats 202 stagnants" valeur={sante.etats202} alerte={sante.etats202 > 0} />
      <Kpi classes={classes} label="Cameras silencieuses" valeur={sante.camerasSilencieuses} alerte={sante.camerasSilencieuses > 0} />
      <Kpi classes={classes} label="Poller" valeur={sante.pollerStatut} alerte={sante.pollerStatut !== 'succes'} />
    </div>
  </Paper>
);

const SectionVersions = ({ versions, classes }) => (
  <Paper className={classes.paper}>
    <Typography variant="h6">APK a surveiller</Typography>
    <Typography variant="body2" className={classes.muted}>
      Versions qui expliquent possiblement des ecarts recents de stabilite.
    </Typography>
    <div className={classes.tableScroll}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Version</TableCell>
            <TableCell>Appareils</TableCell>
            <TableCell>Incidents</TableCell>
            <TableCell>202 actifs</TableCell>
            <TableCell>Uploads bloques</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {versions.slice(0, 10).map((version) => (
            <TableRow key={version.version_apk || version.version || 'n/d'}>
              <TableCell><strong>{version.version_apk || version.version || 'n/d'}</strong></TableCell>
              <TableCell>{nombre(version.nb_appareils || version.nb_cameras)}</TableCell>
              <TableCell>{nombre(version.nb_incidents)}</TableCell>
              <TableCell>{nombre(version.nb_202_actifs)}</TableCell>
              <TableCell>{nombre(version.nb_uploads_bloques)}</TableCell>
            </TableRow>
          ))}
          {versions.length === 0 && (
            <TableRow><TableCell colSpan={5}>Aucune version fragile exposee.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  </Paper>
);

const Kpi = ({ classes, label, valeur, alerte, onClick }) => (
  <div
    className={`${classes.blocScore} ${onClick ? classes.blocScoreCliquable : ''}`}
    style={alerte ? { borderColor: '#f59e0b', backgroundColor: '#fff7ed' } : null}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={(event) => {
      if (onClick && (event.key === 'Enter' || event.key === ' ')) onClick();
    }}
  >
    <div className={classes.label}>{label}</div>
    <div className={classes.valeur}>{valeur}</div>
  </div>
);

function extraireEvenementPrincipal(incident, timeline = []) {
  const candidats = [incident].concat(timeline || []);
  for (let index = 0; index < candidats.length; index += 1) {
    const item = candidats[index];
    const evenementBrut = extraireEvenementDepuisTexte(ligneBruteIncident(item));
    if (evenementBrut && !estTypeGenerique(evenementBrut)) return evenementBrut;
  }

  for (let index = 0; index < candidats.length; index += 1) {
    const item = candidats[index] || {};
    const payload = normaliserPayload(item.payload || item.details || item.extra);
    const valeur = lirePremierChamp(item, payload, [
      'event',
      'evenement',
      'eventName',
      'event_type',
      'runtime_event',
      'typeEvenement',
      'evenement_type',
      'type_projection',
    ]);
    if (valeur && !estTypeGenerique(valeur)) return valeur;
  }

  const fallback = incident && (incident.evenement_type || incident.type_projection);
  return fallback && !estTypeGenerique(fallback) ? fallback : 'incident';
}

function extraireEvenementDepuisTexte(texte) {
  const contenu = String(texte || '');
  const patterns = [
    /\bevent\s*=\s*["']?([A-Z0-9_.:-]+)["']?/i,
    /\bevenement\s*=\s*["']?([A-Z0-9_.:-]+)["']?/i,
    /["']event["']\s*:\s*["']([^"']+)["']/i,
    /["']evenement["']\s*:\s*["']([^"']+)["']/i,
  ];

  for (let index = 0; index < patterns.length; index += 1) {
    const match = contenu.match(patterns[index]);
    if (match && match[1]) return match[1];
  }
  return '';
}

function estTypeGenerique(valeur) {
  return ['ERROR', 'ERREUR', 'WARN', 'WARNING', 'INFO', 'DEBUG', 'TRACE', 'INCIDENT', 'SIGNAL'].includes(String(valeur || '').toUpperCase());
}

function severiteLisible(incident) {
  const valeur = String(incident && (incident.severite_operationnelle || incident.severite_projection || incident.niveau || incident.level) || '').toLowerCase();
  if (valeur.includes('critique') || valeur.includes('critical') || valeur.includes('error') || valeur.includes('erreur')) return 'critique';
  if (valeur.includes('warn') || valeur.includes('degrade') || valeur.includes('avert')) return 'warning';
  if (valeur.includes('info')) return 'info';
  return 'warning';
}

function classeTechniqueIncident(incident, timeline = []) {
  const classeDeclaree = lireMetaIncident(incident, ['classe', 'class', 'famille', 'categorie']);
  if (classeDeclaree && !estTypeGenerique(classeDeclaree)) return classeDeclaree;

  const texte = [incident].concat(timeline || []).map(texteIncident).join(' ').toLowerCase();
  const evenement = extraireEvenementPrincipal(incident, timeline).toLowerCase();
  const domaine = lireMetaIncident(incident, ['domaine', 'domain']).toLowerCase();
  const source = lireMetaIncident(incident, ['source', 'origine']).toLowerCase();
  const corpus = `${evenement} ${domaine} ${source} ${texte}`;
  if (corpus.includes('upload') || corpus.includes('publication')) return 'upload';
  if (corpus.includes('recovery') || corpus.includes('recover')) return 'recovery';
  if (corpus.includes('finalize') || corpus.includes('finalisation')) return 'finalize';
  if (corpus.includes('integrite_video') || corpus.includes('raw') || corpus.includes('first_bytes') || corpus.includes('convergence') || corpus.includes('202')) return 'convergence';
  if (corpus.includes('camera') || corpus.includes('recording') || corpus.includes('runtime') || corpus.includes('exception') || corpus.includes('error')) return 'runtime';
  return 'runtime';
}

function ligneBruteIncident(incident) {
  if (!incident) return 'preuve non detaillee';
  const valeur = incident.contenu_brut
    || incident.message_brut
    || incident.message
    || incident.raw
    || incident.remote_path
    || incident.nom_fichier;

  if (valeur && typeof valeur === 'object') return JSON.stringify(valeur, null, 2);
  return String(valeur || JSON.stringify(incident, null, 2));
}

function construireIncidentCopiable(incident, timeline) {
  const metas = metaIncident(incident);
  return [
    'Incident operationnel',
    `Evenement principal: ${extraireEvenementPrincipal(incident, timeline)}`,
    `Severite: ${severiteLisible(incident)}`,
    `Classe: ${classeTechniqueIncident(incident, timeline)}`,
    `Timestamp complet: ${formaterTimestampComplet(incident.ts_event || incident.recu_at)}`,
    `Level: ${metas.level}`,
    `Source: ${metas.source}`,
    `Domaine: ${metas.domaine}`,
    `CameraId: ${metas.cameraId}`,
    '',
    'Ligne brute principale:',
    ligneBruteIncident(incident),
    '',
    'Timeline brute:',
    construireTimelineCopiable(timeline),
  ].join('\n');
}

function construireTimelineCopiable(timeline) {
  return (timeline || []).map((item) => [
    `[${formaterTimestampComplet(item.ts_event || item.recu_at)}]`,
    `level=${metaIncident(item).level}`,
    `event=${extraireEvenementPrincipal(item, [item])}`,
    `source=${metaIncident(item).source}`,
    `domaine=${metaIncident(item).domaine}`,
    `cameraId=${metaIncident(item).cameraId}`,
    ligneBruteIncident(item),
  ].join(' ')).join('\n');
}

function copierTexte(texte) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(texte);
  }

  const zone = document.createElement('textarea');
  zone.value = texte;
  zone.setAttribute('readonly', '');
  zone.style.position = 'fixed';
  zone.style.left = '-9999px';
  document.body.appendChild(zone);
  zone.select();
  document.execCommand('copy');
  document.body.removeChild(zone);
  return Promise.resolve();
}

function metaIncident(incident) {
  return {
    level: lireMetaIncident(incident, ['level', 'niveau']) || 'n/d',
    source: lireMetaIncident(incident, ['source', 'source_log', 'origine']) || 'n/d',
    domaine: lireMetaIncident(incident, ['domaine', 'domain']) || 'n/d',
    cameraId: lireMetaIncident(incident, ['cameraId', 'camera_id', 'cam_id', 'tel_id', 'telId']) || 'n/d',
  };
}

function lireMetaIncident(incident, champs) {
  if (!incident) return '';

  const ligne = ligneBruteIncident(incident);
  for (let index = 0; index < champs.length; index += 1) {
    const valeurTexte = extraireChampDepuisTexte(ligne, champs[index]);
    if (valeurTexte && !estMetaGenerique(valeurTexte)) return valeurTexte;
  }

  const payload = normaliserPayload(incident.payload || incident.details || incident.extra);
  const valeurStructuree = lirePremierChamp(incident, payload, champs);
  if (valeurStructuree && !estMetaGenerique(valeurStructuree)) return valeurStructuree;

  return '';
}

function extraireChampDepuisTexte(texte, champ) {
  const nom = echapperRegExp(champ);
  const contenu = String(texte || '');
  const patterns = [
    new RegExp(`\\b${nom}\\s*=\\s*"([^"]+)"`, 'i'),
    new RegExp(`\\b${nom}\\s*=\\s*'([^']+)'`, 'i'),
    new RegExp(`\\b${nom}\\s*=\\s*([^\\s,;|]+)`, 'i'),
    new RegExp(`["']${nom}["']\\s*:\\s*["']([^"']+)["']`, 'i'),
  ];

  for (let index = 0; index < patterns.length; index += 1) {
    const match = contenu.match(patterns[index]);
    if (match && match[1]) return match[1];
  }
  return '';
}

function estMetaGenerique(valeur) {
  const normalisee = String(valeur || '').trim().toUpperCase();
  return ['', 'N/D', 'ND', 'NULL', 'UNDEFINED', 'INCIDENT', 'ERROR', 'ERREUR', 'SIGNAL', 'AUTRE'].includes(normalisee);
}

function echapperRegExp(valeur) {
  return String(valeur).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function calculerSanteRecente(donnees, journeeSelectionnee) {
  const source = donnees || {};
  const periode = construirePeriodeJournee(journeeSelectionnee || jourIsoRelative(-1));
  // Le backend filtre déjà par journée en timezone Toronto — ne pas re-filtrer côté client
  // (les incidents de soirée Toronto ont un ts_event UTC du lendemain et seraient exclus à tort)
  const incidentsRecents = [...(source.incidentsCritiques || source.incidents || [])]
    .sort((a, b) => (timestampIncident(b) || 0) - (timestampIncident(a) || 0));
  const fauxSucces = [...(source.fauxSuccesCritiques || source.fauxSucces || [])]
    .sort((a, b) => (timestampObjet(b) || 0) - (timestampObjet(a) || 0));
  const objetsCritiques = [...(source.objetsCritiques || [])]
    .sort((a, b) => (timestampObjet(b) || 0) - (timestampObjet(a) || 0));
  const journeesCamera = filtrerJourneesPeriode(source.journeesCamera || [], periode);
  const dette = source.detteRuntime || {};
  const versions = source.versionsApk || [];
  const sourcePeriode = {
    ...source,
    objetsCritiques,
    journeesCamera,
  };
  const matchsRecents = construireProjectionMatchsRecents(sourcePeriode, incidentsRecents, fauxSucces);
  const nbMatchsSuspects = matchsRecents.filter((match) => match.anomalies.length > 0).length;
  const nbAnomaliesCouverture = somme(matchsRecents, 'nbAnomalies');
  const uploadsBloques = Number(dette.uploads_sans_progression || 0) + somme(versions, 'nb_uploads_bloques');
  const etats202 = Number(dette.etats_202_stagnants || 0) + somme(versions, 'nb_202_actifs');
  const camerasSilencieuses = (source.cameras || []).filter((camera) => camera.silence_camera).length;
  const nbIncidentsCritiques = incidentsRecents.filter((incident) => (
    String(incident.severite_operationnelle || incident.niveau || '').toLowerCase().includes('critique')
  )).length;
  const detteActive = uploadsBloques + etats202 + fauxSucces.length;
  const penalite = nbAnomaliesCouverture * 18 + nbMatchsSuspects * 10 + nbIncidentsCritiques * 8;
  const score = Math.max(0, Math.min(100, 100 - penalite));

  return {
    score,
    incidentsRecents,
    incidentsGroupes: regrouperIncidentsParEpisode(incidentsRecents),
    nbMatchsSuspects,
    nbAnomaliesCouverture,
    nbIncidentsCritiques,
    nbFauxSucces: fauxSucces.length,
    uploadsBloques,
    etats202,
    detteActive,
    camerasSilencieuses,
    pollerStatut: source.poller && source.poller.statut ? source.poller.statut : 'inconnu',
    matchsRecents,
    camerasPeriode: construireCamerasPeriode(sourcePeriode, incidentsRecents, fauxSucces, matchsRecents, periode),
    versionsFragiles: versions
      .filter((version) => Number(version.nb_incidents || 0) + Number(version.nb_202_actifs || 0) + Number(version.nb_uploads_bloques || 0) > 0)
      .sort((a, b) => scoreVersion(b) - scoreVersion(a)),
  };
}

function construirePeriodeJournee(journee) {
  const jour = /^\d{4}-\d{2}-\d{2}$/.test(String(journee || '')) ? journee : jourIsoRelative(-1);
  const debut = new Date(`${jour}T00:00:00`);
  const fin = new Date(debut.getTime() + 24 * 60 * 60 * 1000);
  return {
    jour,
    debutMs: debut.getTime(),
    finMs: fin.getTime(),
  };
}

function filtrerJourneesPeriode(items, periode) {
  return [...(items || [])]
    .filter((item) => jourIsoDepuisValeur(item && item.journee_captation) === periode.jour)
    .sort((a, b) => (timestampJournee(b) || 0) - (timestampJournee(a) || 0));
}

function regrouperIncidentsParEpisode(incidents) {
  const groupes = [];
  const dernierParCamera = new Map();
  const fenetreMs = 2 * 60 * 1000;

  [...(incidents || [])]
    .sort((a, b) => (timestampIncident(a) || 0) - (timestampIncident(b) || 0))
    .forEach((incident) => {
      const camera = incident.cam_id || incident.tel_id || 'camera-inconnue';
      const ts = timestampIncident(incident) || 0;
      const dernierGroupe = dernierParCamera.get(camera);
      const memeEpisode = dernierGroupe
        && dernierGroupe.camera === camera
        && Math.abs(ts - dernierGroupe.finMs) <= fenetreMs;

      if (memeEpisode) {
        dernierGroupe.incidents.push(incident);
        dernierGroupe.finMs = ts || dernierGroupe.finMs;
        dernierGroupe.representant = incident;
        dernierParCamera.set(camera, dernierGroupe);
        return;
      }

      const groupe = {
        id: `episode-${camera}-${ts || groupes.length}`,
        camera,
        debutMs: ts,
        finMs: ts,
        representant: incident,
        incidents: [incident],
      };
      groupes.push(groupe);
      dernierParCamera.set(camera, groupe);
    });

  return groupes
    .map((groupe) => {
      const evenements = groupe.incidents.map((incident) => extraireEvenementPrincipal(incident, [incident]));
      return {
        ...groupe,
        nbSignaux: groupe.incidents.length,
        libelleScenario: resumerScenarioIncident(evenements),
      };
    })
    .sort((a, b) => (b.finMs || 0) - (a.finMs || 0));
}

function resumerScenarioIncident(evenements) {
  const uniquesEvenements = Array.from(new Set((evenements || []).filter(Boolean)));
  if (uniquesEvenements.length === 0) return 'episode runtime';
  if (uniquesEvenements.length === 1) return uniquesEvenements[0];
  return `${uniquesEvenements[0]} + ${uniquesEvenements.length - 1} autres`;
}

function construireCamerasPeriode(source, incidents, fauxSucces, matchs, periode) {
  const index = new Map();
  const ajouter = (cameraId, sourceSignal, timestamp, details = {}) => {
    if (!cameraId) return;
    const id = String(cameraId);
    if (!index.has(id)) {
      index.set(id, {
        id,
        sources: new Set(),
        nbIncidents: 0,
        nbMatchs: 0,
        dernierSignalAt: timestamp || `${periode.jour}T00:00:00`,
      });
    }
    const camera = index.get(id);
    camera.sources.add(sourceSignal);
    if (details.incident) camera.nbIncidents += 1;
    if (details.match) camera.nbMatchs += 1;
    if (timestampMs(timestamp) > timestampMs(camera.dernierSignalAt)) camera.dernierSignalAt = timestamp;
  };

  (source.journeesCamera || []).forEach((journee) => {
    ajouter(journee.cam_id || journee.tel_id, 'journee camera', journee.dernier_evenement_at || journee.journee_captation);
  });
  (incidents || []).forEach((incident) => {
    ajouter(incident.cam_id || incident.tel_id, 'incident', incident.ts_event || incident.recu_at, { incident: true });
  });
  (fauxSucces || []).forEach((item) => {
    ajouter(cameraDepuisObjet(item), 'faux succes', item.dernier_observe_at || item.derniere_preuve_utile_at || item.recu_at);
  });
  (source.objetsCritiques || []).forEach((item) => {
    ajouter(cameraDepuisObjet(item), 'objet critique', item.dernier_observe_at || item.recu_at);
  });
  (matchs || []).forEach((match) => {
    (match.cameras || []).forEach((cameraId) => {
      ajouter(cameraId, 'match', match.dernierSignalAt, { match: true });
    });
  });

  return Array.from(index.values())
    .map((camera) => ({
      ...camera,
      sources: Array.from(camera.sources).sort(),
    }))
    .sort((a, b) => (timestampMs(b.dernierSignalAt) || 0) - (timestampMs(a.dernierSignalAt) || 0));
}

function cameraDepuisObjet(item) {
  const payload = normaliserPayload(item && item.payload);
  return lirePremierChamp(item, payload, ['cam_id', 'camId', 'cameraId', 'camera_id', 'tel_id', 'telId']);
}

function filtrerDerniersJours(items, lireTimestamp, jours) {
  const seuil = Date.now() - jours * 24 * 60 * 60 * 1000;
  return [...(items || [])]
    .filter((item) => {
      const ts = lireTimestamp(item);
      return ts === null || ts >= seuil;
    })
    .sort((a, b) => (lireTimestamp(b) || 0) - (lireTimestamp(a) || 0));
}

function timestampIncident(incident) {
  return timestampMs(incident && (incident.ts_event || incident.recu_at));
}

function timestampObjet(item) {
  return timestampMs(item && (item.dernier_observe_at || item.derniere_preuve_utile_at || item.recu_at));
}

function timestampJournee(item) {
  return timestampMs(item && (item.dernier_evenement_at || item.journee_captation));
}

function timestampMs(valeur) {
  if (!valeur) return null;
  const date = new Date(valeur);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
}

function jourIsoRelative(decalageJours) {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + decalageJours);
  return date.toISOString().slice(0, 10);
}

function decalerJourIso(journee, decalageJours) {
  const date = new Date(`${journee || jourIsoRelative(-1)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return jourIsoRelative(-1);
  date.setDate(date.getDate() + decalageJours);
  return date.toISOString().slice(0, 10);
}

function jourIsoDepuisValeur(valeur) {
  if (!valeur) return '';
  const texte = String(valeur);
  if (/^\d{4}-\d{2}-\d{2}/.test(texte)) return texte.slice(0, 10);
  const date = new Date(texte);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

function ageDepuis(valeur) {
  const ts = timestampMs(valeur);
  if (!ts) return 'n/d';
  const secondes = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (secondes < 60) return `${secondes}s`;
  if (secondes < 3600) return `${Math.floor(secondes / 60)}m`;
  if (secondes < 86400) return `${Math.floor(secondes / 3600)}h`;
  return `${Math.floor(secondes / 86400)}j`;
}

function formaterHeure(valeur) {
  const ts = timestampMs(valeur);
  if (!ts) return '--:--';
  const date = new Date(ts);
  return date.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
}

function formaterTimestampComplet(valeur) {
  const ts = timestampMs(valeur);
  if (!ts) return 'n/d';
  return new Date(ts).toLocaleString('fr-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function idIncident(incident, index = 0) {
  if (!incident) return `incident-${index}`;
  return String(
    incident.ligne_id
    || incident.id
    || `${incident.tel_id || incident.cam_id || 'camera'}-${incident.ts_event || incident.recu_at || index}-${incident.evenement_type || incident.type_projection || 'incident'}`
  );
}

function idEpisodeIncident(incident, index = 0) {
  if (!incident) return `episode-${index}`;
  return String(incident.id || idIncident(representantIncident(incident), index));
}

function representantIncident(incident) {
  if (!incident) return null;
  return incident.representant || incident;
}

function urlInvestigationCamera(incident) {
  const representant = representantIncident(incident);
  const camera = representant && (representant.cam_id || representant.tel_id);
  if (!camera) {
    return `/cockpit/investigation?q=${encodeURIComponent(requeteInvestigation(representant))}`;
  }

  const params = new URLSearchParams();
  params.set('camera', camera);
  const journee = journeeIncident(representant);
  if (journee) params.set('journee', journee);
  const incidentId = representant && (representant.ligne_id || representant.id);
  if (incidentId) params.set('incident', incidentId);
  if (incident && incident.id) params.set('episode', incident.id);
  const debut = incident && incident.debutMs ? new Date(incident.debutMs).toISOString() : '';
  const fin = incident && incident.finMs ? new Date(incident.finMs).toISOString() : '';
  if (debut) params.set('debut', debut);
  if (fin) params.set('fin', fin);
  const evenement = extraireEvenementPrincipal(representant, incident && incident.incidents ? incident.incidents : [representant]);
  if (evenement) params.set('event', evenement);
  return `/cockpit/investigation?${params.toString()}`;
}

function urlInvestigationCameraDepuisCamera(camera, journee) {
  const params = new URLSearchParams();
  params.set('camera', camera.id);
  if (journee) params.set('journee', journee);
  return `/cockpit/investigation?${params.toString()}`;
}

function requeteInvestigation(incident) {
  return incident && (incident.cam_id || incident.tel_id || incident.ligne_id || incident.evenement_type || incident.type_projection) || '';
}

function journeeIncident(incident) {
  const ts = timestampMs(incident && (incident.ts_event || incident.recu_at));
  if (!ts) return '';
  return new Date(ts).toISOString().slice(0, 10);
}

function construireTimelineIncident(incident, incidents) {
  const representant = representantIncident(incident);
  if (incident && Array.isArray(incident.incidents) && incident.incidents.length > 0) {
    return [...incident.incidents]
      .sort((a, b) => (timestampMs(a.ts_event || a.recu_at) || 0) - (timestampMs(b.ts_event || b.recu_at) || 0));
  }
  const camera = representant.cam_id || representant.tel_id;
  const centre = timestampMs(representant.ts_event || representant.recu_at);
  const fenetreMs = 90 * 60 * 1000;

  return [...(incidents || [])]
    .filter((item) => {
      const memeCamera = !camera || item.cam_id === camera || item.tel_id === camera || item.cam_id === representant.tel_id || item.tel_id === representant.cam_id;
      const ts = timestampMs(item.ts_event || item.recu_at);
      if (!memeCamera) return false;
      if (!centre || !ts) return idIncident(item) === idIncident(representant);
      return Math.abs(ts - centre) <= fenetreMs;
    })
    .sort((a, b) => (timestampMs(a.ts_event || a.recu_at) || 0) - (timestampMs(b.ts_event || b.recu_at) || 0))
    .slice(0, 12);
}

function interpreterIncident(incident, timeline) {
  const texte = timeline.concat([incident]).map(texteIncident).join(' ').toLowerCase();
  const evenement = extraireEvenementPrincipal(incident, timeline).toLowerCase();
  const classe = classeTechniqueIncident(incident, timeline).toLowerCase();
  const corpus = `${evenement} ${classe} ${texte}`;
  const pistes = [];
  const recoveryObserve = corpus.includes('recovery');
  const restartObserve = corpus.includes('restart') || corpus.includes('reboot') || corpus.includes('redemarr');
  const uploadTouche = corpus.includes('upload') || corpus.includes('publication');
  const convergenceAbsente = corpus.includes('202') || corpus.includes('stagn') || corpus.includes('convergence') || corpus.includes('progression_absente') || corpus.includes('integrite_video');
  const silenceCamera = corpus.includes('silence') || corpus.includes('recording_lost') || corpus.includes('perdue') || corpus.includes('recording_state');
  const videoTouchee = corpus.includes('video') || corpus.includes('raw') || corpus.includes('first_bytes');

  if (silenceCamera) pistes.push('Camera possiblement arretee ou etat recording divergent.');
  if (restartObserve) pistes.push('Runtime possiblement redemarre pendant la capture.');
  if (recoveryObserve) pistes.push('Recovery observe: verifier qu une preuve utile revient apres le signal.');
  if (videoTouchee && convergenceAbsente) pistes.push('Capture probablement interrompue ou preuve video incomplete.');
  if (uploadTouche) pistes.push('Publication ou upload a verifier: la video peut etre absente cote client.');
  if (videoTouchee && !uploadTouche) pistes.push('Video potentiellement perdue ou angle manquant selon la suite de la timeline.');
  if (pistes.length === 0) pistes.push('Incident probablement mineur: surveillance recommandee sur la prochaine occurrence.');

  return {
    convergence: convergenceAbsente ? 'incertaine' : 'probable',
    recovery: recoveryObserve ? 'observe' : 'non indique',
    pistes,
  };
}

function recommanderPistes(incident, interpretation) {
  const recommandations = ['Surveiller la prochaine occurrence'];
  const texte = texteIncident(incident).toLowerCase();
  const pistes = interpretation.pistes.join(' ').toLowerCase();
  if (pistes.includes('recording') || pistes.includes('arretee')) recommandations.unshift('Verifier si la camera enregistre encore.');
  if (pistes.includes('runtime') || pistes.includes('redemarre')) recommandations.unshift('Verifier stabilite runtime camera.');
  if (pistes.includes('recovery')) recommandations.unshift('Confirmer le retour de recovery avec une preuve video.');
  if (interpretation.convergence !== 'probable') recommandations.unshift('Verifier RAW, FIRST_BYTES ou preuve utile suivante.');
  if (texte.includes('upload') || texte.includes('publication')) recommandations.unshift('Verifier publication client et upload backend.');
  if (texte.includes('memoire') || texte.includes('memory') || texte.includes('raw')) recommandations.unshift('Verifier stockage camera.');
  return Array.from(new Set(recommandations)).slice(0, 5);
}

function texteIncident(incident) {
  if (!incident) return '';
  return [
    incident.evenement_type,
    incident.type_projection,
    incident.categorie_operationnelle,
    incident.severite_operationnelle,
    incident.niveau,
    incident.contenu_brut,
    incident.message_brut,
    incident.message,
    incident.raw,
    incident.remote_path,
    incident.nom_fichier,
  ].filter(Boolean).join(' ');
}

function periodeIncident(timeline) {
  if (!timeline || timeline.length === 0) return 'n/d';
  const debut = timestampMs(timeline[0].ts_event || timeline[0].recu_at);
  const fin = timestampMs(timeline[timeline.length - 1].ts_event || timeline[timeline.length - 1].recu_at);
  if (!debut || !fin) return 'n/d';
  if (debut === fin) return formaterHeure(debut);
  return `${formaterHeure(debut)} - ${formaterHeure(fin)}`;
}

function dureeTimeline(timeline) {
  if (!timeline || timeline.length < 2) return 'instant';
  const debut = timestampMs(timeline[0].ts_event || timeline[0].recu_at);
  const fin = timestampMs(timeline[timeline.length - 1].ts_event || timeline[timeline.length - 1].recu_at);
  if (!debut || !fin) return 'n/d';
  return formatDuree(Math.floor((fin - debut) / 1000));
}

function formatDuree(secondes) {
  if (secondes < 60) return `${secondes}s`;
  if (secondes < 3600) return `${Math.floor(secondes / 60)}m`;
  const heures = Math.floor(secondes / 3600);
  const minutes = Math.floor((secondes % 3600) / 60);
  return minutes > 0 ? `${heures}h${minutes}m` : `${heures}h`;
}

function classeStabilite(stabilite, classes) {
  if (stabilite === 'stable') return classes.chipVert;
  if (stabilite === 'critique' || stabilite === 'suspect') return classes.chipRouge;
  return classes.chipOrange;
}

function construireProjectionMatchsRecents(source, incidentsRecents, fauxSucces) {
  const signaux = collecterSignauxCouverture(source, incidentsRecents, fauxSucces);
  const indexMatch = new Map();

  signaux.forEach((signal) => {
    if (!signal.matchId) return;
    const match = assurerMatch(indexMatch, signal.matchId, signal.timestamp);
    if (signal.cameraId) match.cameras.add(signal.cameraId);
    if (signal.videoId) match.videos.add(signal.videoId);
    if (signal.eventId || signal.clipId) {
      const evenement = assurerEvenement(match, signal.eventId || signal.clipId, signal.timestamp);
      if (signal.cameraId) evenement.cameras.add(signal.cameraId);
      if (signal.videoId) {
        evenement.videos.add(signal.videoId);
        if (signal.cameraId) {
          evenement.videosParCamera.set(signal.cameraId, (evenement.videosParCamera.get(signal.cameraId) || 0) + 1);
        }
      }
      if (signal.clipId) evenement.aClip = true;
      if (signal.estBut) evenement.estBut = true;
      evenement.signaux += 1;
    }
  });

  const projections = Array.from(indexMatch.values()).map(projeterMatch);
  if (projections.length > 0) {
    return projections.sort((a, b) => timestampMs(b.dernierSignalAt) - timestampMs(a.dernierSignalAt));
  }

  return construireProjectionJournees(source.journeesCamera || []);
}

function collecterSignauxCouverture(source, incidentsRecents, fauxSucces) {
  return []
    .concat(incidentsRecents || [])
    .concat(fauxSucces || [])
    .concat(source.objetsCritiques || [])
    .map(extraireSignalCouverture)
    .filter((signal) => signal && (signal.matchId || signal.eventId || signal.clipId || signal.videoId));
}

function extraireSignalCouverture(item) {
  if (!item) return null;
  const payload = normaliserPayload(item.payload);
  const texte = [
    item.evenement_type,
    item.type_projection,
    item.categorie_operationnelle,
    item.libelle,
    item.contenu_brut,
    JSON.stringify(payload || {}),
  ].filter(Boolean).join(' ');

  return {
    matchId: lirePremierChamp(item, payload, ['match_id', 'matchId', 'match', 'idMatch']),
    eventId: lirePremierChamp(item, payload, ['event_id', 'eventId', 'evenementId', 'correlationId', 'correlation_id']),
    clipId: lirePremierChamp(item, payload, ['clip_id', 'clipId', 'clip', 'sequenceId', 'sequence_id']),
    videoId: lirePremierChamp(item, payload, ['video_id', 'videoId', 'video', 'mediaId', 'media_id']),
    cameraId: lirePremierChamp(item, payload, ['cam_id', 'camId', 'cameraId', 'camera_id', 'tel_id', 'telId']),
    timestamp: item.ts_event || item.recu_at || item.dernier_observe_at || item.derniere_preuve_utile_at,
    estBut: estEvenementBut(texte),
  };
}

function assurerMatch(indexMatch, matchId, timestamp) {
  if (!indexMatch.has(matchId)) {
    indexMatch.set(matchId, {
      id: matchId,
      libelle: `Match ${matchId}`,
      cameras: new Set(),
      videos: new Set(),
      evenements: new Map(),
      dernierSignalAt: timestamp,
    });
  }

  const match = indexMatch.get(matchId);
  if (timestampMs(timestamp) > timestampMs(match.dernierSignalAt)) {
    match.dernierSignalAt = timestamp;
  }
  return match;
}

function assurerEvenement(match, eventId, timestamp) {
  if (!match.evenements.has(eventId)) {
    match.evenements.set(eventId, {
      id: eventId,
      cameras: new Set(),
      videos: new Set(),
      videosParCamera: new Map(),
      aClip: false,
      estBut: false,
      signaux: 0,
      dernierSignalAt: timestamp,
    });
  }

  const evenement = match.evenements.get(eventId);
  if (timestampMs(timestamp) > timestampMs(evenement.dernierSignalAt)) {
    evenement.dernierSignalAt = timestamp;
  }
  return evenement;
}

function projeterMatch(match) {
  const anomalies = [];
  const evenements = Array.from(match.evenements.values());
  const cameras = Array.from(match.cameras);

  if (match.cameras.size === 1 && (match.videos.size > 0 || evenements.length > 0)) {
    anomalies.push({
      cle: 'match_une_camera',
      libelle: 'une seule camera detectee',
      niveau: 'critique',
      description: `Le match ne montre qu'une camera (${cameras[0] || 'n/d'}), alors que la couverture attend normalement plusieurs angles.`,
    });
  }

  evenements.forEach((evenement) => {
    if (evenement.videos.size > 0 && evenement.cameras.size === 1) {
      anomalies.push({
        cle: `angle_unique_${evenement.id}`,
        libelle: 'evenement avec angle unique',
        niveau: 'warning',
        description: `L'evenement ${evenement.id} a de la video, mais une seule camera contribue.`,
      });
    }
    evenement.videosParCamera.forEach((nbVideos, cameraId) => {
      if (nbVideos > 1) {
        anomalies.push({
          cle: `doublon_${evenement.id}_${cameraId}`,
          libelle: 'deux videos meme camera',
          niveau: 'critique',
          description: `L'evenement ${evenement.id} contient ${nbVideos} videos pour la camera ${cameraId}; cela peut masquer un angle manquant.`,
        });
      }
    });
    if (evenement.aClip && evenement.videos.size === 0) {
      anomalies.push({
        cle: `clip_sans_video_${evenement.id}`,
        libelle: 'clip sans video',
        niveau: 'critique',
        description: `Un clip est reference pour l'evenement ${evenement.id}, mais aucune preuve video associee n'est visible.`,
      });
    }
    if (evenement.estBut && evenement.videos.size === 0) {
      anomalies.push({
        cle: `but_sans_video_${evenement.id}`,
        libelle: 'but sans video',
        niveau: 'critique',
        description: `L'evenement ${evenement.id} ressemble a un but, sans video detectee.`,
      });
    }
  });

  const anomaliesUniques = dedoublonnerAnomalies(anomalies);
  return {
    id: match.id,
    libelle: match.libelle,
    cameras,
    nbCameras: match.cameras.size,
    nbEvenements: evenements.length,
    nbVideos: match.videos.size,
    nbAnomalies: anomaliesUniques.length,
    anomalies: anomaliesUniques,
    etatGeneral: anomaliesUniques.some((anomalie) => anomalie.niveau === 'critique')
      ? 'suspect'
      : anomaliesUniques.length > 0 ? 'a surveiller' : 'stable',
    dernierSignalAt: match.dernierSignalAt,
  };
}

function construireProjectionJournees(journees) {
  return filtrerDerniersJours(journees, timestampJournee, 3).map((journee) => {
    const anomalies = [];
    if (journee.stabilite && journee.stabilite !== 'stable') {
      anomalies.push({
        cle: 'journee_degradee',
        libelle: `journee ${journee.stabilite}`,
        niveau: journee.stabilite === 'critique' ? 'critique' : 'warning',
        description: 'La projection journee camera indique une stabilite degradee.',
      });
    }
    if (Number(journee.nb_video_perdue || 0) > 0) {
      anomalies.push({
        cle: 'video_perdue',
        libelle: 'video perdue',
        niveau: 'critique',
        description: `${nombre(journee.nb_video_perdue)} video(s) perdue(s) dans cette journee camera.`,
      });
    }
    if (Number(journee.nb_video_incident || 0) > 0) {
      anomalies.push({
        cle: 'video_incident',
        libelle: 'video incident',
        niveau: 'warning',
        description: `${nombre(journee.nb_video_incident)} incident(s) video dans cette journee camera.`,
      });
    }
    const cameraId = journee.cam_id || journee.tel_id;

    return {
      id: `${journee.journee_captation}-${journee.cam_id || journee.tel_id}`,
      libelle: journee.journee_captation || 'Journee camera',
      cameras: cameraId ? [cameraId] : [],
      nbCameras: journee.cam_id || journee.tel_id ? 1 : 0,
      nbEvenements: nombreTotalJournee(journee),
      nbVideos: 'n/d',
      nbAnomalies: anomalies.length,
      anomalies,
      etatGeneral: anomalies.some((anomalie) => anomalie.niveau === 'critique')
        ? 'suspect'
        : anomalies.length > 0 ? 'a surveiller' : 'stable',
      dernierSignalAt: journee.dernier_evenement_at || journee.journee_captation,
      sourceFallback: true,
    };
  });
}

function nombreTotalJournee(journee) {
  return Number(journee.nb_incidents_critiques || 0)
    + Number(journee.nb_video_incident || 0)
    + Number(journee.nb_video_perdue || 0)
    + Number(journee.nb_recovery || 0);
}

function dedoublonnerAnomalies(anomalies) {
  const index = new Map();
  anomalies.forEach((anomalie) => {
    if (!index.has(anomalie.cle)) index.set(anomalie.cle, anomalie);
  });
  return Array.from(index.values());
}

function normaliserPayload(payload) {
  if (!payload) return {};
  if (typeof payload === 'object') return payload;
  try {
    return JSON.parse(payload);
  } catch (erreur) {
    return {};
  }
}

function lirePremierChamp(item, payload, champs) {
  for (let index = 0; index < champs.length; index += 1) {
    const champ = champs[index];
    if (item && item[champ] !== undefined && item[champ] !== null && item[champ] !== '') return String(item[champ]);
    if (payload && payload[champ] !== undefined && payload[champ] !== null && payload[champ] !== '') return String(payload[champ]);
  }
  return '';
}

function estEvenementBut(texte) {
  return /\b(goal|but)\b/i.test(texte || '');
}

function classeSeverite(incident, classes) {
  const valeur = String(incident.severite_operationnelle || incident.niveau || '').toLowerCase();
  if (valeur.includes('critique')) return classes.chipRouge;
  if (valeur.includes('degrade') || valeur.includes('warn')) return classes.chipOrange;
  return classes.chipGris;
}

function scoreVersion(version) {
  return Number(version.nb_incidents || 0) * 4
    + Number(version.nb_202_actifs || 0) * 3
    + Number(version.nb_uploads_bloques || 0) * 3;
}

function somme(items, champ) {
  return (items || []).reduce((total, item) => total + Number(item[champ] || 0), 0);
}

function nombre(valeur) {
  if (valeur === null || valeur === undefined || valeur === '') return '0';
  return String(valeur);
}

export default CockpitSanteRecente;



// WEBPACK FOOTER //
// src/components/cockpit/CockpitSanteRecente.js